import { TFunction } from 'i18next';

const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

/** Tiered elapsed-time label — a raw MM:SS with no upper bound reads as "243:37" for a
 *  delegation that has been running for hours. Picks a single dominant unit instead of
 *  combining them — minutes under an hour, hours under a day, days beyond that — with
 *  i18next's count-based plural resolution (`_one`/`_other`) handling the days tier's
 *  "1 dia"/"2 dias" split; minutes/hours read the same in both languages regardless of
 *  count, so those two keys carry no plural suffix. Shared by
 *  useAgentDelegationConversationModalHeader (modal header) and useElapsedSince (agent
 *  presence card/chip/panel). */
export function formatElapsedMs(elapsedMs: number, t: TFunction): string {
  const totalMinutes = Math.max(
    0,
    Math.floor(elapsedMs / (MS_PER_SECOND * SECONDS_PER_MINUTE)),
  );
  if (totalMinutes === 0) {
    return t('doBox.delegation.conversation.elapsedLessThanMinute');
  }
  if (totalMinutes < MINUTES_PER_HOUR) {
    return t('doBox.delegation.conversation.elapsedMinutes', {
      count: totalMinutes,
    });
  }
  const totalHours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  if (totalHours < HOURS_PER_DAY) {
    return t('doBox.delegation.conversation.elapsedHours', {
      count: totalHours,
    });
  }
  const totalDays = Math.floor(totalHours / HOURS_PER_DAY);
  return t('doBox.delegation.conversation.elapsedDays', { count: totalDays });
}
